'use client'
import * as React from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

/**
 * Welch's z-test for two proportions (unpooled variance). Mirrors
 * `abSignificance()` in nextapp/lib/publicToolsMath.ts — that file's public
 * white-label calculator, NOT nextapp/lib/stats.ts (the product's internal,
 * pooled-variance A/B-testing engine; a different formula despite the
 * similar name — see idukki-web's CLAUDE.md, "Public calculator tools").
 * Keep this function's output identical to publicToolsMath.ts's copy.
 */
function zTestTwoProportions(visA: number, conA: number, visB: number, conB: number) {
  if (!visA || !visB) return null
  const pA = conA / visA
  const pB = conB / visB
  const lift = pA === 0 ? 0 : (pB - pA) / pA
  const seA = (pA * (1 - pA)) / visA
  const seB = (pB * (1 - pB)) / visB
  const se = Math.sqrt(seA + seB)
  if (se === 0) return null
  const z = (pB - pA) / se
  // Two-tailed p from normal CDF approx (Abramowitz & Stegun 26.2.17)
  const pNorm = (x: number) => {
    const t = 1 / (1 + 0.2316419 * Math.abs(x))
    const d = 0.3989423 * Math.exp(-x * x / 2)
    const p =
      d *
      t *
      (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))))
    return x > 0 ? 1 - p : p
  }
  const pValue = 2 * (1 - pNorm(Math.abs(z)))
  // Required sample size per arm at alpha=0.05, power=0.8 to detect current effect.
  const meanP = (pA + pB) / 2
  const mde = Math.abs(pB - pA)
  const sampleNeeded =
    mde > 0
      ? Math.ceil((2 * meanP * (1 - meanP) * Math.pow(1.96 + 0.842, 2)) / (mde * mde))
      : Infinity
  return { pA, pB, lift, z, pValue, sampleNeeded }
}

export function AbCalculator() {
  const [visA, setVisA] = React.useState(5000)
  const [conA, setConA] = React.useState(150)
  const [visB, setVisB] = React.useState(5000)
  const [conB, setConB] = React.useState(195)
  const r = zTestTwoProportions(visA, conA, visB, conB)

  function reset() {
    setVisA(5000); setConA(150); setVisB(5000); setConB(195)
  }

  const significant = r ? r.pValue < 0.05 : false
  const winner = r ? (r.pB > r.pA ? 'Variant B' : 'Variant A') : null
  const haveEnough = r ? (visA + visB) >= r.sampleNeeded * 2 : false

  return (
    <div className="grid gap-8 md:grid-cols-2">
      <div className="rounded-md border bg-card p-7">
        <h2 className="font-display text-xl font-medium">Inputs</h2>
        <p className="mt-1 text-sm text-muted-foreground">Two variants. Conversion = any goal event (purchase, add-to-cart, sign-up).</p>
        <div className="mt-6 grid grid-cols-2 gap-4">
          <Field label="A: visitors" value={visA} onChange={setVisA} />
          <Field label="A: conversions" value={conA} onChange={setConA} />
          <Field label="B: visitors" value={visB} onChange={setVisB} />
          <Field label="B: conversions" value={conB} onChange={setConB} />
        </div>
        <Button variant="outline" className="mt-5" onClick={reset}>Reset to example</Button>
      </div>

      <div className="rounded-md border bg-card p-7">
        <h2 className="font-display text-xl font-medium">Result</h2>
        {!r ? (
          <p className="mt-3 text-sm text-muted-foreground">Enter sample sizes &gt; 0 in both arms.</p>
        ) : (
          <div className="mt-4 space-y-5">
            <Stat label="Variant A conversion" value={(r.pA * 100).toFixed(2) + '%'} />
            <Stat label="Variant B conversion" value={(r.pB * 100).toFixed(2) + '%'} />
            <Stat label="Relative lift" value={(r.lift >= 0 ? '+' : '') + (r.lift * 100).toFixed(2) + '%'} tone={r.lift >= 0 ? 'good' : 'bad'} />
            <Stat label="p-value (two-tailed)" value={r.pValue.toFixed(4)} tone={significant ? 'good' : 'neutral'} />
            <Stat label="z-score" value={r.z.toFixed(2)} />
            <Stat label="Sample needed per arm (α=0.05, power=0.8)" value={Number.isFinite(r.sampleNeeded) ? r.sampleNeeded.toLocaleString() : ','} />
            <div
              className={`rounded-md border p-4 text-sm ${
                significant
                  ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-900 dark:text-emerald-200'
                  : 'border-amber-500/30 bg-amber-500/10 text-amber-900 dark:text-amber-200'
              }`}
            >
              {significant ? (
                <>
                  <strong>{winner} wins</strong> with statistical significance ({(r.lift * 100 * (r.pB > r.pA ? 1 : -1)).toFixed(2)}% relative lift, p = {r.pValue.toFixed(4)}). You can declare a winner.
                </>
              ) : (
                <>
                  <strong>Not yet significant.</strong>{' '}
                  {haveEnough
                    ? 'You’ve hit the sample target but the variants are too close, the true effect may be smaller than detectable.'
                    : `Run for ~${(r.sampleNeeded * 2 - visA - visB).toLocaleString()} more visitors total to reach 80% power.`}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function Field({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <label className="flex flex-col gap-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <Input
        type="number"
        min={0}
        value={value}
        onChange={(e) => onChange(Math.max(0, parseInt(e.target.value || '0', 10)))}
      />
    </label>
  )
}

function Stat({ label, value, tone = 'neutral' }: { label: string; value: string | number; tone?: 'good' | 'bad' | 'neutral' }) {
  const toneCls =
    tone === 'good' ? 'text-emerald-600 dark:text-emerald-400'
    : tone === 'bad' ? 'text-rose-600 dark:text-rose-400'
    : 'text-foreground'
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/60 pb-2 last:border-b-0 last:pb-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={`font-display text-lg font-medium ${toneCls}`}>{value}</span>
    </div>
  )
}
